import React, { useState } from "react";
import {
    Category,
    CategoryParameter,
    ParameterDataType,
    SelectionType,
} from "../../types/Category";
import { Unit, UnitDimension, UnitSystem } from "../../types/Unit";
import ParameterList from "./ParameterList";
import SimilarNamesEditor from "./SimilarNamesEditor";
import DoubleUnitsEditor from "./DoubleUnitsEditor";
import SelectableOptionEditor from "../filters/SelectableOptionEditor";
import { logApiError, getUserMessage } from "../../api/apiError";

interface NewParam {
    name: string;
    description: string;
    dataType: ParameterDataType;
    unitId?: string;        // INTEGER: optional single unit
    unitIds?: string[];     // DOUBLE: one or more allowed units
    selectables?: string[];
    selectionType?: SelectionType;
}

interface CategoryFormProps {
    category: Category;
    isLoading: boolean;
    units?: Unit[];
    dimensions?: UnitDimension[];
    systems?: UnitSystem[];

    onSaveCategoryNameUpdate: (newName: string) => Promise<void>;

    onSaveCategoryDescriptionUpdate: (
        newDescription: string
    ) => Promise<void>;

    onAddSimilarName?: (name: string) => Promise<void>;
    onRemoveSimilarName?: (name: string) => Promise<void>;
    onChangeServiceArea?: (hasServiceArea: boolean) => Promise<void>;
    onChangeItemLocation?: (hasItemLocation: boolean) => Promise<void>;
    onMoveParameter?: (index: number, delta: -1 | 1) => Promise<void>;

    onParameterAdd: () => void;
    showNewParamForm: boolean;
    onNewParamCancel: () => void;
    onParameterRemove: (index: number) => void;

    onParameterChange: (
        index: number,
        field: keyof CategoryParameter | "options",
        value: string | ParameterDataType | string[]
    ) => void;

    onSaveParameterNameUpdate: (
        index: number,
        parameter: CategoryParameter,
        newName: string
    ) => Promise<void>;

    onSaveParameterDescriptionUpdate: (
        index: number,
        parameter: CategoryParameter,
        newDescription: string
    ) => Promise<void>;

    onCategoryFieldChange?: (field: "name" | "description", value: string) => void;
    onCategorySave?: () => Promise<void>;

    onSaveParameterUnitUpdate?: (
        index: number,
        parameter: CategoryParameter,
        newUnitIds: string[]
    ) => Promise<void>;

    onSaveOptionEdit?: (paramIndex: number, choiceId: number, newName: string) => Promise<void>;
    onRemoveOption?: (paramIndex: number, choiceId: number) => Promise<void>;

    newParam: NewParam;

    onNewParamChange: (
        field: keyof NewParam,
        value: string | ParameterDataType | string[] | SelectionType
    ) => void;

    onNewParamSubmit: () => Promise<void>;
}

type CategoryEditableField = "name" | "description" | null;

const CategoryForm: React.FC<CategoryFormProps> = ({
                                                       category,
                                                       isLoading,
                                                       units = [],
                                                       dimensions = [],
                                                       systems = [],
                                                       onSaveCategoryNameUpdate,
                                                       onSaveCategoryDescriptionUpdate,
                                                       onAddSimilarName,
                                                       onRemoveSimilarName,
                                                       onChangeServiceArea,
                                                       onChangeItemLocation,
                                                       onMoveParameter,
                                                       onCategoryFieldChange,
                                                       onCategorySave,
                                                       onParameterAdd,
                                                       showNewParamForm,
                                                       onNewParamCancel,
                                                       onParameterRemove,
                                                       onParameterChange,
                                                       onSaveParameterNameUpdate,
                                                       onSaveParameterDescriptionUpdate,
                                                       onSaveParameterUnitUpdate,
                                                       onSaveOptionEdit,
                                                       onRemoveOption,
                                                       newParam,
                                                       onNewParamChange,
                                                       onNewParamSubmit,
                                                   }) => {
    const isCreating = !category.id;
    const [editingCategoryField, setEditingCategoryField] =
        useState<CategoryEditableField>(null);

    const [categoryDraftValue, setCategoryDraftValue] = useState("");
    const [categoryNameError, setCategoryNameError] = useState<string | null>(null);
    const [categoryDescError, setCategoryDescError] = useState<string | null>(null);
    const [newParamError, setNewParamError] = useState<string | null>(null);
    const [createError, setCreateError] = useState<string | null>(null);

    const startCategoryFieldEdit = (
        field: "name" | "description",
        value: string
    ) => {
        setEditingCategoryField(field);
        setCategoryDraftValue(value);
        if (field === "name") setCategoryNameError(null);
        if (field === "description") setCategoryDescError(null);
    };

    const cancelCategoryFieldEdit = () => {
        setEditingCategoryField(null);
        setCategoryDraftValue("");
        setCategoryNameError(null);
        setCategoryDescError(null);
    };

    const saveCategoryFieldEdit = async () => {
        if (!editingCategoryField) {
            return;
        }

        const trimmedValue = categoryDraftValue.trim();

        try {
            if (editingCategoryField === "name") {
                if (!trimmedValue) {
                    setCategoryNameError("Category name is required.");
                    return;
                }
                await onSaveCategoryNameUpdate(trimmedValue);
            }

            if (editingCategoryField === "description") {
                await onSaveCategoryDescriptionUpdate(trimmedValue);
            }

            setEditingCategoryField(null);
            setCategoryDraftValue("");
        } catch (err) {
            logApiError(err);
            if (editingCategoryField === "name") setCategoryNameError(getUserMessage(err));
            if (editingCategoryField === "description") setCategoryDescError(getUserMessage(err));
        }
    };

    return (
        <div className="section-box">
            <h3 style={{ marginBottom: "1.5rem" }}>
                {category.id ? "Edit Category" : "Create New Category"}
            </h3>

            <div className="category-form">
                <div className="form-group">
                    <label htmlFor="categoryName">Name</label>

                    {isCreating ? (
                        <input
                            type="text"
                            id="categoryName"
                            name="name"
                            value={category.name}
                            onChange={(e) => onCategoryFieldChange?.("name", e.target.value)}
                            disabled={isLoading}
                            placeholder="Enter category name"
                        />
                    ) : (
                        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                            <input
                                type="text"
                                id="categoryName"
                                name="name"
                                value={
                                    editingCategoryField === "name"
                                        ? categoryDraftValue
                                        : category.name
                                }
                                onChange={(e) => setCategoryDraftValue(e.target.value)}
                                disabled={isLoading || editingCategoryField !== "name"}
                                placeholder="Enter category name"
                            />

                            {editingCategoryField === "name" ? (
                                <>
                                    <button
                                        type="button"
                                        className="btn-sm"
                                        onClick={saveCategoryFieldEdit}
                                        disabled={isLoading}
                                    >
                                        Save
                                    </button>

                                    <button
                                        type="button"
                                        className="btn-sm"
                                        onClick={cancelCategoryFieldEdit}
                                        disabled={isLoading}
                                    >
                                        Cancel
                                    </button>
                                </>
                            ) : (
                                <button
                                    type="button"
                                    className="btn-sm"
                                    onClick={() => startCategoryFieldEdit("name", category.name)}
                                    disabled={isLoading || editingCategoryField !== null}
                                >
                                    Edit
                                </button>
                            )}
                        </div>
                    )}
                    {categoryNameError && (
                        <div className="error-message" style={{ marginTop: "0.35rem" }}>{categoryNameError}</div>
                    )}
                </div>

                <div className="form-group">
                    <label htmlFor="categoryDescription">Description</label>

                    {isCreating ? (
                        <textarea
                            id="categoryDescription"
                            name="description"
                            value={category.description ?? ""}
                            onChange={(e) => onCategoryFieldChange?.("description", e.target.value)}
                            disabled={isLoading}
                            placeholder="Enter description"
                            rows={3}
                        />
                    ) : (
                        <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
                        <textarea
                            id="categoryDescription"
                            name="description"
                            value={
                                editingCategoryField === "description"
                                    ? categoryDraftValue
                                    : category.description ?? ""
                            }
                            onChange={(e) => setCategoryDraftValue(e.target.value)}
                            disabled={isLoading || editingCategoryField !== "description"}
                            placeholder="Enter description"
                            rows={3}
                        />

                        {editingCategoryField === "description" ? (
                            <>
                                <button
                                    type="button"
                                    className="btn-sm"
                                    onClick={saveCategoryFieldEdit}
                                    disabled={isLoading}
                                >
                                    Save
                                </button>

                                <button
                                    type="button"
                                    className="btn-sm"
                                    onClick={cancelCategoryFieldEdit}
                                    disabled={isLoading}
                                >
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                className="btn-sm"
                                onClick={() =>
                                    startCategoryFieldEdit(
                                        "description",
                                        category.description ?? ""
                                    )
                                }
                                disabled={isLoading || editingCategoryField !== null}
                            >
                                Edit
                            </button>
                        )}
                    </div>
                    )}
                    {categoryDescError && (
                        <div className="error-message" style={{ marginTop: "0.35rem" }}>{categoryDescError}</div>
                    )}
                </div>

                {onAddSimilarName && onRemoveSimilarName && (
                    <div className="form-group" style={{ marginTop: "0.75rem" }}>
                        <label>Similar names (aliases to help users find this category)</label>
                        <SimilarNamesEditor
                            values={category.similarNames ?? []}
                            onAdd={onAddSimilarName}
                            onRemove={onRemoveSimilarName}
                            disabled={isLoading}
                        />
                    </div>
                )}

                {onChangeServiceArea && (
                    <div className="form-group" style={{ marginTop: "0.75rem" }}>
                        <label style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", cursor: "pointer" }}>
                            <input
                                type="checkbox"
                                checked={!!category.hasServiceArea}
                                onChange={(e) => onChangeServiceArea(e.target.checked)}
                                disabled={isLoading}
                            />
                            Service category — listings declare a service-area coverage (e.g. towing, mobile mechanics)
                        </label>
                    </div>
                )}

                {onChangeItemLocation && (
                    <div className="form-group" style={{ marginTop: "0.5rem" }}>
                        <label style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", cursor: "pointer" }}>
                            <input
                                type="checkbox"
                                checked={!!category.hasItemLocation}
                                onChange={(e) => onChangeItemLocation(e.target.checked)}
                                disabled={isLoading}
                            />
                            Fixed-location category — each listing sits at a location (e.g. room, apartment, house for rent or sale)
                        </label>
                    </div>
                )}
            </div>

            <ParameterList
                parameters={category.parameters}
                units={units}
                dimensions={dimensions}
                systems={systems}
                onAdd={onParameterAdd}
                onRemove={onParameterRemove}
                onChange={onParameterChange}
                onSaveNameUpdate={onSaveParameterNameUpdate}
                onSaveDescriptionUpdate={onSaveParameterDescriptionUpdate}
                onSaveUnitUpdate={onSaveParameterUnitUpdate}
                onSaveOptionEdit={onSaveOptionEdit}
                onRemoveOption={onRemoveOption}
                onMove={onMoveParameter}
                disabled={false}
            />

            {!showNewParamForm && (
                <div style={{ marginTop: "1rem" }}>
                    <button
                        type="button"
                        onClick={onParameterAdd}
                        disabled={isLoading}
                    >
                        + Add Parameter
                    </button>
                </div>
            )}

            {showNewParamForm && (
                <div
                    style={{
                        borderTop: "1px solid var(--border-color)",
                        marginTop: "1.5rem",
                        paddingTop: "1.5rem",
                    }}
                >
                    <h4 style={{ marginBottom: "1rem" }}>New Parameter</h4>

                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "1rem",
                            maxWidth: 400,
                        }}
                    >
                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label>Parameter Name</label>
                            <input
                                placeholder="Enter parameter name"
                                value={newParam.name}
                                onChange={(e) =>
                                    onNewParamChange("name", e.target.value)
                                }
                                disabled={isLoading}
                            />
                        </div>

                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label>Description</label>
                            <input
                                placeholder="Enter description"
                                value={newParam.description}
                                onChange={(e) =>
                                    onNewParamChange("description", e.target.value)
                                }
                                disabled={isLoading}
                            />
                        </div>

                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label>Data Type</label>
                            <select
                                value={newParam.dataType}
                                onChange={(e) =>
                                    onNewParamChange(
                                        "dataType",
                                        e.target.value as ParameterDataType
                                    )
                                }
                                disabled={isLoading}
                            >
                                {Object.values(ParameterDataType).map((dt) => (
                                    <option key={dt} value={dt}>
                                        {dt}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {newParam.dataType === ParameterDataType.SELECTABLE && (
                            <div
                                style={{
                                    padding: "1rem",
                                    background: "var(--bg-secondary)",
                                    borderRadius: "var(--radius-md)",
                                }}
                            >
                                <div
                                    style={{
                                        fontWeight: 500,
                                        marginBottom: "0.75rem",
                                    }}
                                >
                                    Selection Type
                                </div>

                                <div
                                    style={{
                                        display: "flex",
                                        gap: "1rem",
                                        marginBottom: "1rem",
                                    }}
                                >
                                    <label
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <input
                                            type="radio"
                                            checked={
                                                newParam.selectionType ===
                                                SelectionType.SINGLE
                                            }
                                            onChange={() =>
                                                onNewParamChange(
                                                    "selectionType",
                                                    SelectionType.SINGLE
                                                )
                                            }
                                        />
                                        Single
                                    </label>

                                    <label
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            cursor: "pointer",
                                        }}
                                    >
                                        <input
                                            type="radio"
                                            checked={
                                                newParam.selectionType ===
                                                SelectionType.MULTI
                                            }
                                            onChange={() =>
                                                onNewParamChange(
                                                    "selectionType",
                                                    SelectionType.MULTI
                                                )
                                            }
                                        />
                                        Multiple
                                    </label>
                                </div>

                                <div>
                                    <div
                                        style={{
                                            fontWeight: 500,
                                            marginBottom: "0.5rem",
                                        }}
                                    >
                                        Options
                                    </div>

                                    <SelectableOptionEditor
                                        options={newParam.selectables ?? []}
                                        onChange={(opts) =>
                                            onNewParamChange("selectables", opts)
                                        }
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>
                        )}

                        {newParam.dataType === ParameterDataType.INTEGER && (
                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label>Unit (optional)</label>
                                <select
                                    value={newParam.unitId ?? ""}
                                    onChange={(e) => onNewParamChange("unitId", e.target.value)}
                                    disabled={isLoading}
                                >
                                    <option value="">— none —</option>
                                    {units.map((u) => (
                                        <option key={u.id} value={u.id}>{u.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {newParam.dataType === ParameterDataType.DOUBLE && (
                            <div className="form-group" style={{ marginBottom: 0 }}>
                                <label>Units (pick a dimension, then one unit per system)</label>
                                <DoubleUnitsEditor
                                    units={units}
                                    dimensions={dimensions}
                                    systems={systems}
                                    value={newParam.unitIds ?? []}
                                    onChange={(ids) => onNewParamChange("unitIds", ids as any)}
                                    disabled={isLoading}
                                />
                            </div>
                        )}

                        <div className="button-group" style={{ marginTop: "0.5rem" }}>
                            <button
                                type="button"
                                onClick={async () => {
                                    setNewParamError(null);
                                    try {
                                        await onNewParamSubmit();
                                    } catch (err) {
                                        logApiError(err);
                                        setNewParamError(getUserMessage(err));
                                    }
                                }}
                                disabled={isLoading || !newParam.name.trim()}
                            >
                                Save Parameter
                            </button>

                            <button
                                type="button"
                                onClick={() => { setNewParamError(null); onNewParamCancel(); }}
                                disabled={isLoading}
                                className="btn-secondary"
                            >
                                Cancel
                            </button>
                        </div>
                        {newParamError && (
                            <div className="error-message" style={{ marginTop: "0.5rem" }}>{newParamError}</div>
                        )}
                    </div>
                </div>
            )}

            {isCreating && (
                <div style={{ marginTop: "1.5rem", borderTop: "1px solid var(--border-color)", paddingTop: "1.5rem" }}>
                    <button
                        type="button"
                        onClick={async () => {
                            setCreateError(null);
                            try {
                                await onCategorySave?.();
                            } catch (err) {
                                logApiError(err);
                                setCreateError(getUserMessage(err));
                            }
                        }}
                        disabled={isLoading || !category.name.trim()}
                    >
                        Create Category
                    </button>
                    {createError && (
                        <div className="error-message" style={{ marginTop: "0.5rem" }}>{createError}</div>
                    )}
                </div>
            )}

        </div>
    );
};

export default CategoryForm;